import { CircularProgress, Paper, Typography } from "@mui/material"
import React, { useMemo } from "react"

import { gql, useQuery } from "@apollo/client"
import TankChart from "./TankChart"

const GET_TANK_DATA = gql`
  query TankObs($lastWeek: timestamptz!) {
    geo_location_by_pk(id: 11) {
      id
      name
      type
      geometry_type
      metadata
      observations(where: { date_added: { _gte: $lastWeek } }) {
        id
        data
        date_added
      }
    }
  }
`

// Extracted component for tank status display
const TankStatus = ({ location, message, showChart = false, observations = [] }) => (
  <Paper>
    <Typography variant="h4" style={{ paddingLeft: 20 }}>
      {location?.name || "Louis' Fish Tank"}
    </Typography>
    {location?.type && (
      <Typography variant="caption" style={{ paddingLeft: 20 }} color="textSecondary">
        Type: {location.type} | Geometry: {location.geometry_type}
      </Typography>
    )}
    <Typography variant="body1" style={{ paddingLeft: 20 }}>
      {message}
    </Typography>
    {showChart && <TankChart data={observations} />}
  </Paper>
)

export default function Tasks() {
  const sevenDaysAgo = useMemo(() => {
    return new Date(new Date().setDate(new Date().getDate() - 7)).toISOString()
  }, [])
  
  const { data, loading, error } = useQuery(GET_TANK_DATA, {
    variables: { lastWeek: sevenDaysAgo },
  })

  if (loading) return <CircularProgress />
  if (error) return <Typography>{error.message}</Typography>
  if (!data || !data.geo_location_by_pk || !data.geo_location_by_pk.observations) {
    return <TankStatus 
      location={data?.geo_location_by_pk} 
      message="Tank offline or no data available" 
    />
  }

  const location = data.geo_location_by_pk
  const observations = location.observations.filter(
    (x) => x.data.temperature > 10 && x.data.temperature < 30
  )

  if (observations.length === 0) {
    return <TankStatus 
      location={location} 
      message="No valid temperature readings found" 
    />
  }
  // get the min and max of the last 24 data points
  // return the difference
  const last24Hours = observations.slice(-24)
  const last24HoursTemps = last24Hours.map((o) => o.data.temperature)
  const minTemp = Math.min(...last24HoursTemps)
  const maxTemp = Math.max(...last24HoursTemps)
  const twentyFourHourDeviation = (maxTemp - minTemp).toFixed(2)
  const last48Hours = observations.slice(-48)
  const last48HoursTemps = last48Hours.map((o) => o.data.temperature)
  const minTemp48 = Math.min(...last48HoursTemps)
  const maxTemp48 = Math.max(...last48HoursTemps)
  const fourtyEightHourDeviation = (maxTemp48 - minTemp48).toFixed(2)
  // get the min and max of the last 7 days
  const sevenDayTemps = observations.map((o) => o.data.temperature)
  const sevenDayMin = Math.min(...sevenDayTemps).toFixed(2)
  const sevenDayMax = Math.max(...sevenDayTemps).toFixed(2)

  return (
    <Paper>
      <Typography variant="h4" style={{ paddingLeft: 20 }}>
        {location.name}
      </Typography>
      <Typography variant="caption" style={{ paddingLeft: 20 }} color="textSecondary" display="block">
        Type: {location.type} | Geometry: {location.geometry_type}
      </Typography>
      {location.metadata?.purpose && (
        <Typography variant="caption" style={{ paddingLeft: 20 }} color="textSecondary" display="block">
          Purpose: {location.metadata.purpose}
        </Typography>
      )}
      <Typography variant="caption" style={{ paddingLeft: 20 }} gutterBottom display="block">
        7 Day Max {sevenDayMax}°C
      </Typography>
      <Typography variant="caption" style={{ paddingLeft: 20 }} gutterBottom display="block">
        7 Day Min {sevenDayMin}°C
      </Typography>
      <Typography variant="caption" style={{ paddingLeft: 20 }} gutterBottom display="block">
        Current Temperature:{" "}
        {observations[observations.length - 1].data.temperature}°C
      </Typography>
      <Typography variant="caption" style={{ paddingLeft: 20 }} gutterBottom display="block">
        24 Hour Deviation: {twentyFourHourDeviation}°C
      </Typography>
      <Typography variant="caption" style={{ paddingLeft: 20 }} gutterBottom display="block">
        48 Hour Deviation: {fourtyEightHourDeviation}°C
      </Typography>
      <TankChart data={observations} />
    </Paper>
  )
}
